import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export interface FooterLinkItem {
  label: string;
  /** Internal route. Mutually exclusive with `href` and `render`. */
  to?: string;
  /** External URL. Renders as <a target="_blank">. */
  href?: string;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Tailwind override for hover color (defaults to foreground). */
  hoverClass?: string;
  /** Escape hatch for fully custom items (e.g. auth button). */
  render?: () => ReactNode;
}

interface FooterLinkColumnProps {
  title: string;
  links: FooterLinkItem[];
  /** Render the link list across N columns to reduce vertical height. */
  columns?: 1 | 2;
}

const FooterLinkColumn = ({ title, links, columns = 1 }: FooterLinkColumnProps) => {
  const listClass =
    columns === 2
      ? "grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-mono text-muted-foreground"
      : "space-y-2 text-xs font-mono text-muted-foreground";

  // Tap-target bump: WCAG 2.5.8 wants ≥24×24px. The base text is 16px
  // tall, so we add vertical padding + an inline-block to guarantee the
  // hit area is at least 24px even when the label is short ("Learn").
  const tapTargetClass = "inline-block py-1 min-h-[24px]";

  return (
    <div className="space-y-2">
      {/* h2 (not h4) so the page heading order stays sequential —
          jumping from <h1> to <h4> trips Lighthouse / axe heading-order. */}
      <h2 className="text-[11px] font-mono uppercase tracking-wider text-foreground">
        {title}
      </h2>
      <ul className={listClass}>
        {links.map((link, idx) => {
          if (link.render) {
            return <li key={idx}>{link.render()}</li>;
          }

          const hover = link.hoverClass ?? "hover:text-foreground";
          const className = link.icon
            ? `inline-flex items-center gap-1.5 ${hover} transition-colors ${tapTargetClass}`
            : `${hover} transition-colors ${tapTargetClass}`;

          if (link.href) {
            return (
              <li key={idx}>
                <a
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : undefined}
                  rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className={className}
                >
                  {link.icon}
                  {link.label}
                </a>
              </li>
            );
          }

          return (
            <li key={idx}>
              <Link to={link.to ?? "#"} className={className}>
                {link.icon}
                {link.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FooterLinkColumn;
